// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useState } from 'react';

import {
  type SavedGridStateJsonString,
  type TableView,
  type TableViewId,
  type TableViewType,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { Button, Divider, Flex, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GridApi } from 'ag-grid-enterprise';

import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { CreateOrEditTableViewModal } from 'helpers/ag-grid/TableViewPanel/CreateOrEditTableViewModal';
import { loadSavedGridState } from 'helpers/ag-grid/TableViewPanel/loadSavedGridState';
import { TableViewOption } from 'helpers/ag-grid/TableViewPanel/TableViewOption';
import { useTableViews } from 'helpers/ag-grid/TableViewPanel/useTableViews';
import { stringifyGridStateJson } from 'helpers/ag-grid/TableViewPanel/utils';
import { isNotNil } from 'helpers/isNotNil';
import { setLocalStorageWrapped } from 'hooks/useLocalStorage';
import { useHasAccess } from 'hooks/user/useHasAccess';

type Props = {
  gridApi: GridApi;
  tableViewType: TableViewType;
};

export const TableViewPanel = ({ gridApi, tableViewType }: Props) => {
  const hasModifySavedViewsAccess = useHasAccess('savedViews', 'write');
  const [creating, setCreating] = useState(false);
  const [tableViewToRename, setTableViewToRename] = useState<TableView | null>(null);
  const [tableViewToDelete, setTableViewToDelete] = useState<TableView | null>(null);
  const createOrEditModalOpened = isNotNil(tableViewToRename) || creating;
  const {
    tableViews,
    loading,
    selectedTableViewId,
    setSelectedTableViewId,
    createTableView,
    updateTableView,
    deleteTableView,
    mutationRunning,
    defaultTableViewId,
    onChangeDefaultTableViewId,
  } = useTableViews(tableViewType);

  const resetTableView = useCallback(() => {
    setSelectedTableViewId(null);
    gridApi.resetColumnState();
    gridApi.setFilterModel(null);
  }, [gridApi, setSelectedTableViewId]);

  const { apiCall: getTableViewApiCall, loading: loadingTableView } = useEvolveGet('admin/tableView/:tableViewId');
  const onLoadGridState = useCallback(
    async (gridState: SavedGridStateJsonString) => {
      loadSavedGridState(gridState, gridApi, true);
    },
    [gridApi],
  );

  const selectTableView = useCallback(
    async (tableViewId: TableViewId) => {
      setSelectedTableViewId(tableViewId);
      const tableView = await getTableViewApiCall({ url: `admin/tableView/${tableViewId}` });
      await onLoadGridState(tableView.gridState);
    },
    [onLoadGridState, getTableViewApiCall, setSelectedTableViewId],
  );

  const handleChangeDefaultTableView = useCallback(
    async (updatedDefaultTableView: TableView | null) => {
      // Update the user preference for default table view ID.
      onChangeDefaultTableViewId(updatedDefaultTableView?.tableViewId ?? null);
      if (!updatedDefaultTableView) {
        // Clear local storage cache for default table view data (consumed by useInitialTableView when we initialize a table).
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
        return;
      }
      // Select and load the new default table view if it isn't already selected.
      const shouldToggleSelection =
        isNotNil(updatedDefaultTableView) && updatedDefaultTableView.tableViewId !== selectedTableViewId;
      // Optimistic UI update while we wait for the grid state to load.
      if (shouldToggleSelection) {
        setSelectedTableViewId(updatedDefaultTableView.tableViewId);
      }
      const tableView = await getTableViewApiCall({ url: `admin/tableView/${updatedDefaultTableView.tableViewId}` });
      // Update local storage cache for default table view data.
      setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, tableView);
      if (shouldToggleSelection) {
        await onLoadGridState(tableView.gridState);
      }
    },
    [
      onChangeDefaultTableViewId,
      selectedTableViewId,
      setSelectedTableViewId,
      getTableViewApiCall,
      onLoadGridState,
      tableViewType,
    ],
  );

  return (
    <>
      <Flex direction="column" h="100%" gap={2}>
        <Stack py="sm" px={8} flex="1 1 auto" gap={4} style={{ overflowY: 'auto' }}>
          {tableViews.length === 0 ? (
            <Text size="sm" c="gray" ta="center" px="sm">
              {loading ? <>Loading...</> : <>No table views have been created.</>}
            </Text>
          ) : (
            tableViews.map((tableView) => (
              <TableViewOption
                key={tableView.tableViewId}
                tableView={tableView}
                checked={selectedTableViewId === tableView.tableViewId}
                loading={selectedTableViewId === tableView.tableViewId && loadingTableView}
                isDefault={defaultTableViewId === tableView.tableViewId}
                onChangeDefaultTableView={handleChangeDefaultTableView}
                onClick={() => {
                  const previouslySelected = tableView.tableViewId === selectedTableViewId;
                  if (previouslySelected) return resetTableView(); // Deselect if already selected.
                  return selectTableView(tableView.tableViewId);
                }}
                onDelete={() => setTableViewToDelete(tableView)}
                onRename={() => {
                  setTableViewToRename(tableView);
                }}
                onSave={() => {
                  const gridState = stringifyGridStateJson(gridApi.getState());
                  void updateTableView(tableView.tableViewId, {
                    gridState,
                  });
                  if (defaultTableViewId === tableView.tableViewId) {
                    // Update local storage cache for default table view data.
                    setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, {
                      ...tableView,
                      gridState,
                    });
                  }
                }}
                canSaveViews={hasModifySavedViewsAccess}
              />
            ))
          )}
        </Stack>
        {hasModifySavedViewsAccess && (
          <Flex direction="column" bg="gray.0">
            <Divider w="100%" />
            <Flex p="xs">
              <Button
                variant="light"
                fullWidth
                onClick={() => setCreating(true)}
                disabled={createOrEditModalOpened || mutationRunning}
              >
                Create table view
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>
      <CreateOrEditTableViewModal
        opened={createOrEditModalOpened}
        onClose={() => {
          setTableViewToRename(null);
          setCreating(false);
        }}
        tableView={tableViewToRename}
        gridApi={gridApi}
        createTableView={createTableView}
        onTableViewCreated={(createdTableView) => {
          notifications.show({
            title: 'Successfully created',
            message: `Table view ${createdTableView.name} created.`,
            color: 'green',
          });
          setSelectedTableViewId(createdTableView.tableViewId);
        }}
        updateTableView={updateTableView}
        onTableViewUpdated={(updatedTableView) => {
          notifications.show({
            title: 'Successfully renamed',
            message: `Table view renamed to ${updatedTableView.name}.`,
            color: 'green',
          });
        }}
      />
      <VariableTrackingModal
        variable={tableViewToDelete}
        title="Delete table view"
        size="sm"
        buttonColor="red"
        confirmationText="Delete"
        onClose={() => setTableViewToDelete(null)}
        onConfirm={async (tableView) => {
          await deleteTableView(tableView.tableViewId).then(() => {
            notifications.show({
              title: 'Successfully deleted',
              message: `Table view "${tableView.name}" deleted.`,
              color: 'green',
            });
            if (selectedTableViewId === tableView.tableViewId) {
              resetTableView();
            }
          });
        }}
      >
        {(tableView) => (
          <Text>
            Are you sure you want to delete table view <b>{tableView.name}</b>?
          </Text>
        )}
      </VariableTrackingModal>
    </>
  );
};
